import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withRouter } from 'react-router';
import { withLocaleContext } from '../../../../hocs/LocaleContext';
import TileCustomLink from '../TileCustomLink';
import HelpMeChoose from '../HelpMeChoose';
import DefaultImage from '../../../../components/DefaultImage';
import CategoryBadge from '../../../../components/CategoryBadge';
import MasterRange from '../MasterRange';
import styles from './index.scss';
import Skeleton from '../skeleton';

const LINK_TAG = 'a';
const makeImgStyle = img => {
    const domImg = img;
    if (!domImg) return;
    const k = img.naturalWidth / img.naturalHeight;
    domImg.style.height = '85px';
    domImg.style.width = `${85 * k}px`;
};
const isDesktop = () => window.innerWidth > 960;
class Tile extends React.Component {
    constructor() {
        super();
        this.contentRef = React.createRef();
        this.imgRef = React.createRef();
        this.state = {
            hover: false,
            imgLoaded: false,
        };
    }
    hoverChange(hover) {
        this.setState({ hover });
    }
    componentDidMount() {
        if (this.imgRef.current) {
            this.imgRef.current.onload = () => {
                makeImgStyle(this.imgRef.current);
                this.setState({ imgLoaded: true });
            };
            this.imgRef.current.onerror = () => {
                this.setState({ imgLoaded: true });
            };
        }
    }
    componentDidUpdate(prevProps) {
        if (prevProps.data.imageUrl !== this.props.data.imageUrl) {
            if (this.props.data.imageUrl) {
                this.setState({
                    imgLoaded: false,
                });
                this.imgRef.current.onload = () => {
                    makeImgStyle(this.imgRef.current);
                    this.setState({ imgLoaded: true });
                };
                this.imgRef.current.onerror = () => {
                    this.setState({ imgLoaded: true });
                };
            } else {
                this.setState({ imgLoaded: false });
            }
        }
    }
    render() {
        const {
            skeleton,
            data: {
                childCategoryLinks,
                description,
                helpMeChooseEnabled,
                helpMeChooseUrl,
                imageUrl,
                pageUrl,
                name,
                range,
                selectorName,
                nameTag,
                badge,
                categoryLevelTag,
                external,
            },
            cc,
            lc,
            history,
            helpMeChoose,
        } = this.props;

        const tileClasses = classNames({
            [styles.tile]: true,
            [styles.tileCategory]: childCategoryLinks.length,
            [styles.hasSkeleton]: skeleton,
            'category-card-analytics': true,
        });
        const linkClasses = classNames({
            [styles.links]: true,
            'subcategory-analytics': true,
        });

        return (
            <div
                className={tileClasses}
                onMouseEnter={() => this.hoverChange(true)}
                onMouseLeave={() => this.hoverChange(false)}
                onClick={({ target }) => {
                    if (skeleton) return;
                    if (target.tagName.toLowerCase() === LINK_TAG) return;
                    if (external) {
                        window.open(pageUrl, '_self');
                        return;
                    }
                    history.push(`/shop/${cc}/${lc}${pageUrl}`);
                }}
                data-selector-name={selectorName}
                data-category-name={nameTag}
                data-category={categoryLevelTag}
            >
                {badge && <CategoryBadge badge={badge} isCard />}
                <div
                    className={styles.streamContent}
                    style={
                        this.state.hover && isDesktop()
                            ? {
                                  height: `${
                                      this.contentRef.current.offsetHeight >= 277
                                          ? this.contentRef.current.offsetHeight
                                          : 277
                                  }px`,
                              }
                            : {}
                    }
                >
                    <div ref={this.contentRef} className={styles.containerContent}>
                        <div>
                            {imageUrl || skeleton ? (
                                <div className={styles.tileImage}>
                                    <Skeleton fadeOut={!skeleton && this.state.imgLoaded} />
                                    <img ref={this.imgRef} alt={name} loading="lazy" src={imageUrl} />
                                </div>
                            ) : (
                                <DefaultImage className={styles.tileImage} />
                            )}
                            <h2 className={styles.tileHeader}>
                                {name}
                                {skeleton && <Skeleton />}
                            </h2>
                            <div className={styles.tileContent}>
                                <div className={styles.tileDescription}>
                                    {skeleton && <Skeleton />}
                                    <div dangerouslySetInnerHTML={{ __html: description }} />
                                </div>
                            </div>
                            <ul className={styles.linkList}>
                                {childCategoryLinks.length
                                    ? childCategoryLinks.map(
                                          (
                                              {
                                                  pageUrl: childPageUrl,
                                                  name: linkTitle,
                                                  nameTag: subCategoryName,
                                                  categoryLevelTag: categoryType,
                                                  external: externalChild,
                                              },
                                              i,
                                          ) => (
                                              <li
                                                  className={linkClasses}
                                                  key={`tileLink-${i}`}
                                                  data-subcategory-name={subCategoryName}
                                                  data-category={categoryType}
                                              >
                                                  {skeleton ? (
                                                      <Skeleton
                                                          withTemplate={true}
                                                          styleForTemplate={{
                                                              width: '100%',
                                                              height: '16px',
                                                              marginBottom: '8px',
                                                          }}
                                                      />
                                                  ) : (
                                                      <TileCustomLink
                                                          url={childPageUrl}
                                                          external={externalChild}
                                                          cc={cc}
                                                          lc={lc}
                                                      >
                                                          {linkTitle}
                                                      </TileCustomLink>
                                                  )}
                                              </li>
                                          ),
                                      )
                                    : null}
                            </ul>
                            {range && (
                                <MasterRange range={range} extendClass={styles.masterRange} />
                            )}
                        </div>

                        {helpMeChooseEnabled && helpMeChooseUrl && (
                            <div className={styles.bottomBlock}>
                                {skeleton && <Skeleton />}
                                {helpMeChooseEnabled && helpMeChooseUrl && (
                                    <HelpMeChoose
                                        link={helpMeChooseUrl}
                                        phrasecode={helpMeChoose}
                                    />
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }
}

Tile.propTypes = {
    data: PropTypes.shape({
        childCategoryLinks: PropTypes.arrayOf(
            PropTypes.shape({
                pageUrl: PropTypes.string.isRequired,
                name: PropTypes.string.isRequired,
                linkType: PropTypes.string.isRequired,
            }),
        ),
        description: PropTypes.string,
        helpMeChooseEnabled: PropTypes.bool.isRequired,
        helpMeChooseUrl: PropTypes.string,
        imageUrl: PropTypes.string,
        pageUrl: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        range: PropTypes.object,
        selectorName: PropTypes.string,
        nameTag: PropTypes.string,
        badge: PropTypes.object,
        categoryLevelTag: PropTypes.string,
        external: PropTypes.bool,
    }),
    level: PropTypes.string,
    skeleton: PropTypes.bool,
    lc: PropTypes.string.isRequired,
    cc: PropTypes.string.isRequired,
    history: PropTypes.object.isRequired,
    helpMeChoose: PropTypes.string.isRequired,
};

export default withRouter(withLocaleContext(Tile));
